import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import {token} from "@atlaskit/tokens";
import type {JourneyProgressAction} from "./types";

/** Max action buttons rendered in the footer. Overflow belongs under a `more` action. */
export const JOURNEY_PROGRESS_CARD_MAX_UPFRONT_ACTIONS = 4;

/** Convention id for the overflow action (dropdown on desktop / sheet on mobile — parent owns that UI). */
export const JOURNEY_PROGRESS_CARD_MORE_ACTION_ID = "more";

/**
 * Shows at most 4 actions.
 * If more than 4 are passed and one has `id: "more"`, keeps the first 3 non-more + More.
 * Parent wires `more.onClick` to open a dropdown or bottom sheet with the overflow items.
 */
export function resolveJourneyProgressUpfrontActions(
    actions: readonly JourneyProgressAction[],
): readonly JourneyProgressAction[] {
    if (actions.length <= JOURNEY_PROGRESS_CARD_MAX_UPFRONT_ACTIONS) {
        return actions;
    }

    const moreAction = actions.find((action) => action.id === JOURNEY_PROGRESS_CARD_MORE_ACTION_ID);
    const otherActions = actions.filter((action) => action.id !== JOURNEY_PROGRESS_CARD_MORE_ACTION_ID);

    if (moreAction !== undefined) {
        return [...otherActions.slice(0, JOURNEY_PROGRESS_CARD_MAX_UPFRONT_ACTIONS - 1), moreAction];
    }

    return actions.slice(0, JOURNEY_PROGRESS_CARD_MAX_UPFRONT_ACTIONS);
}

export function createJourneyProgressMoreAction(args: {
    onClick: () => void;
    label?: string;
    icon?: JourneyProgressAction["icon"];
    isDisabled?: boolean;
}): JourneyProgressAction {
    return {
        id: JOURNEY_PROGRESS_CARD_MORE_ACTION_ID,
        label: args.label ?? "More",
        icon: args.icon ?? <ShowMoreHorizontalIcon label="" />,
        onClick: args.onClick,
        isDisabled: args.isDisabled,
    };
}

function JourneyProgressCardActionButton({action}: {action: JourneyProgressAction}) {
    const isDisabled = action.isDisabled === true;

    return (
        <button
            type="button"
            disabled={isDisabled}
            onClick={(event) => {
                event.stopPropagation();
                if (isDisabled === true) {
                    return;
                }
                action.onClick();
            }}
            className="grid min-w-0 flex-1 grid-rows-[max-content_max-content] justify-items-center gap-y-ds-space-025 border-0 bg-transparent p-0 disabled:cursor-not-allowed disabled:opacity-50"
        >
            <span
                className="grid size-6 shrink-0 place-items-center [&_svg]:size-6"
                style={{color: isDisabled === true ? token("color.icon.disabled") : token("color.icon.accent.blue")}}
            >
                {action.icon}
            </span>
            <span
                className="max-w-full truncate px-ds-space-025 text-center"
                style={{
                    color: isDisabled === true ? token("color.text.disabled") : token("color.text.subtle"),
                    fontFeatureSettings: "'liga' off, 'calt' off",
                    fontFamily: "Inter",
                    fontSize: "var(--Font-Size-Body-S, 12px)",
                    fontStyle: "normal",
                    fontWeight: "var(--Font-Weight-Body-Regular, 400)",
                    lineHeight: "var(--Line-Height-Body-S, 16px)",
                }}
            >
                {action.label}
            </span>
        </button>
    );
}

export function JourneyProgressCardActions({actions}: {actions: readonly JourneyProgressAction[]}) {
    const upfrontActions = resolveJourneyProgressUpfrontActions(actions);

    if (upfrontActions.length === 0) {
        return null;
    }

    return (
        <div
            className="flex w-full min-w-0 items-center border-t border-solid px-ds-space-100 py-ds-space-100"
            style={{borderColor: token("color.border")}}
        >
            <div className="flex w-full min-w-0 items-center justify-center gap-x-ds-space-050">
                {upfrontActions.map((action) => (
                    <JourneyProgressCardActionButton
                        key={action.id}
                        action={action}
                    />
                ))}
            </div>
        </div>
    );
}
